import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Layout } from '@/components/Layout';
import { R, HONEYPOTS } from '@/lib/routes';

import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Generic404 from '@/pages/Generic404';
import Honeypot from '@/pages/Honeypot';
import { GeoGate } from '@/components/GeoGate';
import { RoleplayBanner } from '@/components/RoleplayBanner';

// Dynamic code-splitting: restricted page bundles are only fetched when an
// authorized user actually renders them (the Guarded gate returns a 404 element
// BEFORE the lazy child mounts, so unauthorized browsers never download e.g. the
// admin or legal chunk).
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const NewBooking = lazy(() => import('@/pages/NewBooking'));
const BookingDetail = lazy(() => import('@/pages/BookingDetail'));
const Inventory = lazy(() => import('@/pages/Inventory'));
const Customers = lazy(() => import('@/pages/Customers'));
const CustomerDetail = lazy(() => import('@/pages/CustomerDetail'));
const Accounts = lazy(() => import('@/pages/Accounts'));
const RecentPayments = lazy(() => import('@/pages/RecentPayments'));
const Recovery = lazy(() => import('@/pages/Recovery'));
const Legal = lazy(() => import('@/pages/Legal'));
const Loan = lazy(() => import('@/pages/Loan'));
const Systems = lazy(() => import('@/pages/Systems'));
const EoiHub = lazy(() => import('@/pages/EoiHub'));
const Bookings = lazy(() => import('@/pages/Bookings'));
const Reports = lazy(() => import('@/pages/Reports'));
const LegalCalendar = lazy(() => import('@/pages/LegalCalendar'));
const PricingTracker = lazy(() => import('@/pages/PricingTracker'));
const MigrateBooking = lazy(() => import('@/pages/MigrateBooking'));
const Hub = lazy(() => import('@/pages/Hub'));
const Reminders = lazy(() => import('@/pages/Reminders'));
const AdminOps = lazy(() => import('@/pages/AdminOps'));
const Proposals = lazy(() => import('@/pages/Proposals'));
const Commissions = lazy(() => import('@/pages/Commissions'));
const LegacyMigration = lazy(() => import('@/pages/LegacyMigration'));
const LegacyArchive = lazy(() => import('@/pages/LegacyArchive'));

const FullLoader = () => (
  <div className="kesar-bg flex min-h-screen items-center justify-center">
    <Loader2 className="h-10 w-10 animate-spin text-[#245329]" />
  </div>
);

// Route guard. Unauthenticated OR insufficient-role access renders a generic 404
// (never 401/403) so restricted routes are indistinguishable from missing ones.
const Guarded = ({ need, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Generic404 logScan />;
  if (need === 'admin' && !user.permissions?.is_admin) return <Generic404 logScan />;
  if (need === 'booking:create' && !user.permissions?.can_create_booking) return <Generic404 logScan />;
  if (need === 'reports' && !user.permissions?.can_view_reports) return <Generic404 logScan />;
  return (
    <Layout>
      <GeoGate>
        <Suspense fallback={<FullLoader />}>{children}</Suspense>
      </GeoGate>
    </Layout>
  );
};

// Bare authenticated gate (no CRM sidebar) — used for the Ecosystem Hub gateway
// and standalone ecosystem modules (each does its own module/root access check).
const AuthedBare = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Generic404 logScan />;
  return (
    <GeoGate>
      <Suspense fallback={<FullLoader />}>{children}</Suspense>
    </GeoGate>
  );
};

function AppRouter() {
  const location = useLocation();
  // OAuth return (session_id in URL fragment) is processed first, on any path.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path={R.login} element={<Login />} />

      {/* Honeypot traps for guessable defaults */}
      {HONEYPOTS.map((p) => (
        <Route key={p} path={p} element={<Honeypot />} />
      ))}

      {/* Obfuscated application routes */}
      <Route path={R.hub} element={<AuthedBare><Hub /></AuthedBare>} />
      <Route path={R.adminOps} element={<AuthedBare><AdminOps /></AuthedBare>} />
      <Route path={R.systems} element={<AuthedBare><Systems /></AuthedBare>} />
      <Route path={R.systemsUserManager} element={<AuthedBare><Systems initialTab="usermanager" /></AuthedBare>} />
      <Route path={R.systemsGeoFence} element={<AuthedBare><Systems initialTab="geofence" /></AuthedBare>} />
      <Route path={R.systemsAudit} element={<AuthedBare><Systems initialTab="audit" /></AuthedBare>} />
      <Route path={R.proposalsHub} element={<AuthedBare><Proposals /></AuthedBare>} />
      <Route path={R.commissionsHub} element={<AuthedBare><Commissions /></AuthedBare>} />
      <Route path={R.reminders} element={<Guarded><Reminders /></Guarded>} />
      <Route path={R.overview} element={<Guarded><Dashboard /></Guarded>} />
      <Route path={R.eoiPunch} element={<Guarded need="booking:create"><NewBooking /></Guarded>} />
      <Route path={R.eoiHub} element={<Guarded><EoiHub /></Guarded>} />
      <Route path={R.bookings} element={<Guarded><Bookings /></Guarded>} />
      <Route path={R.migrateBooking} element={<Guarded need="admin"><MigrateBooking /></Guarded>} />
      <Route path={R.legacyMigration} element={<Guarded need="admin"><LegacyMigration /></Guarded>} />
      <Route path={R.legacyArchive} element={<Guarded><LegacyArchive /></Guarded>} />
      <Route path={R.reports} element={<Guarded need="reports"><Reports /></Guarded>} />
      <Route path={R.booking(':id')} element={<Guarded><BookingDetail /></Guarded>} />
      <Route path={R.inventory} element={<Guarded><Inventory /></Guarded>} />
      <Route path={R.customers} element={<Guarded><Customers /></Guarded>} />
      <Route path={R.customer(':id')} element={<Guarded><CustomerDetail /></Guarded>} />
      <Route path={R.recentPayments} element={<Guarded><RecentPayments /></Guarded>} />
      <Route path={R.accounts} element={<Guarded><Accounts /></Guarded>} />
      <Route path={R.recovery} element={<Guarded><Recovery /></Guarded>} />
      <Route path={R.legal} element={<Guarded><Legal /></Guarded>} />
      <Route path={R.legalCalendar} element={<Guarded><LegalCalendar /></Guarded>} />
      <Route path={R.pricingTracker} element={<Guarded><PricingTracker /></Guarded>} />
      <Route path={R.loan} element={<Guarded><Loan /></Guarded>} />

      {/* Anything else: generic 404 + logged scan event */}
      <Route path="*" element={<Generic404 logScan />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RoleplayBanner />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
